<script setup>
/**
 * Keep (mystic page 2026-10-29, `Claude outputs/mystic-page-2026-10-29/API.md`
 * §6; mocks 03/04): one verb for images from others (a Mystic's drop, a
 * lesson's picture, a painting, a card). A small pill that sits on the
 * picture: "✦ Keep", then "✓ Kept" with one quiet note that it is in the
 * Journal, on today's page and the Kept shelf.
 *
 * `keep` is the server's prefilled Keep body (`today_drop.keep`, a
 * `mystic-drops/` row's `keep`, …), posted as is. The server sends none when
 * the viewer is signed out or the image is theirs, and then there is no Keep.
 * Kept is final here: "Remove from Kept" lives in the Journal's ⋯.
 *
 * Usage: <KeepButton :keep="drop.keep" :kept="drop.kept" @kept="drop.kept = true" />
 * inside a `position: relative` picture frame (it sits top right).
 *
 * `compact` (Your Mystics strip 2026-10-30 §4): a smaller pill for the slim
 * drop cards. Its note would be clipped inside the sideways row, so it is not
 * drawn under the pill; it goes out as `note` for the tab's toast instead.
 */
import { ref, watch, onUnmounted } from 'vue'
import { store } from '@/store'
import { keepImage } from '@/api-service'
import { track } from '@/utils/analytics'

const props = defineProps({
  keep: { type: Object, default: null },
  kept: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
})
const emit = defineEmits(['kept', 'note'])

const isKept = ref(!!props.kept)
watch(() => props.kept, (v) => { if (v) isKept.value = true })
const busy = ref(false)
const own = ref(false)
const note = ref('')
let timer = null
function say (t) {
  emit('note', t)
  if (props.compact) return
  note.value = t; clearTimeout(timer); timer = setTimeout(() => { note.value = '' }, 3600)
}
onUnmounted(() => clearTimeout(timer))

async function onKeep () {
  if (isKept.value || busy.value || !props.keep) return
  busy.value = true
  try {
    const r = await keepImage(store.session_id, props.keep)
    if (!r || r.kept !== true) throw new Error('not_kept')
    isKept.value = true
    track('keep_image', { source: props.keep.source || '', kept: true })
    say('Kept. It’s in your Journal, on today’s page and the Kept shelf.')
    emit('kept', r.item || null)
  } catch (e) {
    const reason = e && e.response && e.response.data && e.response.data.reason
    if (reason === 'own') { own.value = true; return }
    say('That couldn’t be kept just now.')
  } finally { busy.value = false }
}
</script>

<template>
  <span v-if="keep && !own" class="keep">
    <button type="button" class="kb" :class="{ on: isKept, sm: compact }" :disabled="busy" :aria-pressed="isKept ? 'true' : 'false'"
            :aria-label="isKept ? 'Kept in your Journal' : 'Keep this in your Journal'" data-testid="keep-btn" @click.stop="onKeep">{{ isKept ? '✓ Kept' : '✦ Keep' }}</button>
    <span v-if="note" class="note" role="status" data-testid="keep-note">{{ note }}</span>
  </span>
</template>

<style scoped>
.keep { position: absolute; top: 10px; right: 10px; z-index: 3; display: flex; flex-direction: column; align-items: flex-end; }
.kb { font-family: 'Cinzel', serif; font-size: 13px; letter-spacing: .04em; padding: 7px 14px; border-radius: 999px; cursor: pointer;
  color: #ffdfa3; background: rgba(20, 8, 40, .78); border: 1px solid rgba(240, 217, 163, .55); box-shadow: 0 4px 14px -4px rgba(0, 0, 0, .8);
  -webkit-backdrop-filter: blur(4px); backdrop-filter: blur(4px); transition: background .2s, color .2s; }
.kb.sm { font-size: 10.5px; padding: 3px 8px; white-space: nowrap; }
.kb:hover:not(.on) { background: rgba(102, 51, 153, .85); }
.kb.on { color: #1a0f2e; background: linear-gradient(180deg, #fff1cf, #f0d9a3); border-color: transparent; cursor: default; }
.kb:disabled:not(.on) { opacity: .7; cursor: default; }
.note { margin-top: 8px; max-width: min(300px, 80vw); padding: 10px 14px; border-radius: 12px; text-align: center;
  font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 15px; line-height: 1.35; color: #f3eaff;
  background: rgba(28, 14, 52, .94); border: 1px solid rgba(195, 169, 255, .3); box-shadow: 0 10px 28px -10px rgba(0, 0, 0, .8); }
</style>
